import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/ltr — EN hub for Long-Term Resident Visa
import { createFileRoute, Link } from "@tanstack/react-router";
import { LTR_SEGMENTS } from "@/data/ltr-segments";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Award, Wallet, CalendarClock, FileText, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/ltr/")({
  head: () => {
    const url = `${SITE}/en/ltr`;
    const title = `LTR Visa Thailand — 10-Year Long-Term Resident (BOI) 2026 | NYC`;
    const desc = `BOI Long-Term Resident Visa: 10-year (5+5), Digital Work Permit, foreign-income tax exemption, 17% flat tax for Highly-Skilled. Wealthy Global Citizen, Pensioner, Work-from-Thailand, HSP, Dependent. Get your checklist in minutes.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/ltr", "en") }, { name: "description", content: ctaDesc(desc, "/en/ltr", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/ltr", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/ltr", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "Thailand LTR visa, Long-Term Resident BOI, Thailand 10 year visa, wealthy pensioner Thailand, highly skilled professional 17%, work from Thailand professional" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/ltr", "/en/ltr")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "en",
              mainEntity: {
                "@type": "ItemList", numberOfItems: LTR_SEGMENTS.length,
                itemListElement: LTR_SEGMENTS.map((s, i) => ({
                  "@type": "ListItem", position: i + 1, name: s.titleEn, url: `${SITE}/en/ltr/${s.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand LTR Visa Application Service",
              serviceType: "Long-Term Resident Visa Application Service (BOI Thailand)",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "39000", priceCurrency: "THB", description: "LTR end-to-end: qualification + BOI submission + visa stamp + Digital Work Permit" },
            }),
          },
          faqPageSchema([
            { q: "What is the LTR visa?", a: "Long-Term Resident Visa — a 10-year (5+5) Thai visa launched by BOI in Sep 2022. Fee 50,000 THB, includes Digital Work Permit, once-yearly 90-day report, and foreign-income tax exemption under Royal Decree 743." },
            { q: "How does LTR differ from DTV?", a: "LTR targets HNWI / pensioners / senior remote professionals / experts with USD 80K+ income and runs 10 years. DTV targets general digital nomads at lower income thresholds and runs 5 years." },
            { q: "What tax rate do HSP holders pay?", a: "Highly-Skilled Professionals may elect a 17% flat personal income tax under Royal Decree No. 743 (B.E. 2565), versus the standard progressive rate that tops out at 35% above THB 5M. The election applies only to Thai-sourced employment income from a BOI-targeted industry employer; passive and foreign income are taxed separately under standard rules and DTA relief." },
            { q: "Is there a minimum days-in-Thailand requirement?", a: "No — LTR carries no minimum stay obligation, unlike Thailand Elite which has no minimum either but unlike standard Non-B/O which requires re-entry permits. The 10-year multiple-entry privilege is valid for the full visa term, and the 90-day report is replaced by a 1-year report at Immigration HQ Chaeng Watthana or via the BOI One-Stop Service Center." },
            { q: "Can I bring my family?", a: "Yes — LTR Dependent visa covers a legal spouse plus children aged 20 or below, up to 4 dependants per principal applicant at THB 50,000 each (10-year validity). Dependants receive identical work-permit-exempt status for non-employment activities, plus 90-day report exemption. Adult children over 20 must apply for their own visa category (e.g. LTR Wealthy Pensioner if eligible)." },

          ]),
        ]),
      ],
    };
  },
  component: LtrHubEn,
});

function LtrHubEn() {
  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="ltr" locale="en" />
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link><span className="mx-2">/</span><span>LTR Visa</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Award className="mr-1 inline h-3 w-3" /> BOI Long-Term Resident · 5 segments
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">LTR Visa — Long-Term Resident (10 years)</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Thailand's premium 10-year visa from BOI — Digital Work Permit, foreign-income tax exemption, and 17% flat tax for the Highly-Skilled. For HNWI, retirees, senior remote professionals, experts, and their families.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">What is the LTR visa and who qualifies?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              The Long-Term Resident visa is a 10-year Thai visa from BOI launched September 2022. Four principal segments: Wealthy Global Citizen (assets ≥ USD 1M + USD 500K Thai investment), Wealthy Pensioner (50+, pension ≥ USD 80K), Work-from-Thailand Professional (senior remote ≥ USD 80K), Highly-Skilled Professional (flat 17% tax) — plus Dependent. BOI fee 50,000 THB per person. Includes Digital Work Permit, once-yearly 90-day report, and foreign-income tax exemption under Royal Decree 743.
            </p>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-4 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><CalendarClock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Validity</div><div className="text-sm font-bold">10 yrs (5+5)</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">BOI fee</div><div className="text-sm font-bold">THB 50,000</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><FileText className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">90-day report</div><div className="text-sm font-bold">Once/yr</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><ShieldCheck className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Work permit</div><div className="text-sm font-bold">Digital, free</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/dtv" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <FileText className="h-4 w-4" /> Compare with DTV (5 yr)
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">5 LTR segments</h2>
          <p className="mt-2 text-sm text-muted-foreground">Pick the segment that matches your profile · Click for criteria, docs and pricing</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {LTR_SEGMENTS.map((s) => (
              <Link
                key={s.slug}
                to="/en/ltr/$segment"
                params={{ segment: s.slug }}
                className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                data-track-zone="ltr-hub-en"
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{s.icon}</span>
                  <div className="font-bold text-sm">{s.titleEn}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{s.taglineEn}</div>
                <div className="mt-3 text-[11px] font-semibold text-foreground/80">{s.qualifyEn}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">THB {s.servicePriceTHB.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    View <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold">Related services</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Visa Matrix — compare 7 visas", d: "DTV vs LTR vs Non-O in 60 seconds", to: "/en/visa-matrix" as const },
              { t: "DTV (5 years)", d: "Alternative for digital nomads", to: "/en/dtv" as const },
              { t: "All Visa Types", d: "Non-B / Retirement / Marriage", to: "/en/visa" as const },
              { t: "Visa Compliance", d: "TM30 / 90-day / Re-entry", to: "/en/visa-compliance" as const },
              { t: "Work Permit", d: "If working for a Thai company", to: "/en/work-permit" as const },
              { t: "Driving License", d: "DLT 5-year via LTR / Cert Residence", to: "/en/driving-license" as const },
              { t: "MFA + Apostille", d: "Legalise foreign documents", to: "/en/mfa-legalisation" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
